import { UserOutlined } from "@ant-design/icons";
import { Avatar, Dropdown } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ApiAuth from "../../api/auth";
import useNotification from "../notification/notification";
import { logOutOfPortal } from "../../common/utils/logout-sequence";
import { clearUser, selectUser } from "../../store/slices/user";
import "./styles.css";

import EditProfileIcon from "../../assets/edit-profile.svg";
import ExitIcon from "../../assets/exit.svg";
import KeyIcon from "../../assets/key.svg";
import PasskeyIcon from "../../assets/auth/fingerprint.svg";

import {
  setIsOpenProfile,
  setProfilePlacement,
  setProfileUser,
} from "../../store/slices/user_management";

import ChangePasswordForm from "../profile/forms/change-password-form";
import InviteUserModal from "../modals/invitations/user/invite-user";
import Profile from "../profile/profile";

// ✅ use your existing modal
import PasskeysModal from "../modals/passkeys/passkeys-modal";

const ProfileBar = () => {
  const user = useSelector(selectUser);
  const { avatarUrl } = user;

  const dispatch = useDispatch();
  const navigation = useNavigate();
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const [isOpenUserModal, setIsOpenUserModal] = useState(false);
  const [isOpenChangePasswordModal, setIsOpenChangePasswordModal] = useState(false);

  // ✅ passkeys modal state (only open/close here)
  const [isOpenPasskeysModal, setIsOpenPasskeysModal] = useState(false);

  const closePasswordChangeModal = () => setIsOpenChangePasswordModal(false);
  const closeInviteUserModal = () => setIsOpenUserModal(false);

  const logout = async () => {
    /**
     * LF360-1970: the server first, and only then the screen.
     *
     * `logout-sequence.js` carries why. In short: the cookie is cleared by the
     * server or not at all, so showing a logged-out screen after a failed
     * revocation is a lie a reload undoes — on a shared computer, into somebody
     * else's session. A bearer left over from before the cookie release is
     * cleared from this browser either way, and has to be declined while it is
     * still in localStorage, because that is where the header is built from.
     */
    const loggedOut = await logOutOfPortal({
      endSession: ApiAuth.endSession,
      legacyToken: localStorage.getItem("user_token"),
      legacyLogout: ApiAuth.logout,
    });

    if (!loggedOut) {
      openNotification({
        type: "error",
        message: <p>{t("login.logout_failed")}</p>,
      });

      return;
    }

    localStorage.removeItem("user_token");
    dispatch(clearUser());
    navigation("/login");
  };

  const openProfile = () => {
    dispatch(setProfilePlacement("left"));
    dispatch(setProfileUser(user));
    dispatch(setIsOpenProfile(true));
  };

  const onClick = ({ key }) => {
    switch (key) {
      case "1":
        return openProfile();
      case "2":
        return setIsOpenChangePasswordModal(true);
      case "3":
        return logout();
      case "4":
        return setIsOpenPasskeysModal(true);
      default:
        return null;
    }
  };

  // ❗do not change labels/titles (as requested)
  const items = [
    {
      key: "1",
      label: "Edit Profile",
      icon: <img src={EditProfileIcon} alt="edit" />,
    },
    {
      key: "2",
      label: "Change Password",
      icon: <img src={KeyIcon} alt="key" />,
    },
    {
      key: "4",
      label: "Passkeys",
      icon: (
        <img
          src={PasskeyIcon || KeyIcon}
          alt="passkey"
          style={{ width: 18, height: 18 }}
        />
      ),
    },
    {
      key: "3",
      label: "Logout",
      icon: <img src={ExitIcon} alt="exit" />,
      "data-testid": "profile-menu-logout",
    },
  ];

  const avatarImage = avatarUrl ? (
    <img src={avatarUrl} alt="avatar" className="profile-bar__avatar" />
  ) : (
    <Avatar icon={<UserOutlined />} />
  );

  return (
    <>
      <Profile />
      <InviteUserModal
        scope="organization"
        isOpen={isOpenUserModal}
        close={closeInviteUserModal}
      />

      <ChangePasswordForm
        t={t}
        isOpen={isOpenChangePasswordModal}
        close={closePasswordChangeModal}
      />

      <PasskeysModal
        open={isOpenPasskeysModal}
        onClose={() => setIsOpenPasskeysModal(false)}
        t={t}
      />

      <div className="profile-bar">
        <Dropdown
          menu={{ items, onClick }}
          placement="topRight"
          trigger={["click"]}
          styles={{ root: { width: 245 } }}
        >
          <div className="profile-bar__user-container" role="presentation" data-testid="profile-menu">
            {avatarImage}
          </div>
        </Dropdown>
      </div>
    </>
  );
};

export default ProfileBar;